import { useCallback, useEffect, useMemo, useState } from 'react'

import { SIDE_PANEL_PORTAL_ID } from '~/constants'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

const updateColumnSelection = (
  columns: ColumnSelectorColumn[],
  label: string
): ColumnSelectorColumn[] =>
  columns.map(column =>
    column.label === label && !column.isReadonly
      ? { ...column, isSelected: !column.isSelected }
      : column
  )

const clearSelectableColumns = (
  columns: ColumnSelectorColumn[]
): ColumnSelectorColumn[] =>
  columns.map(column =>
    column.isReadonly ? column : { ...column, isSelected: false }
  )

const selectSelectableColumns = (
  columns: ColumnSelectorColumn[]
): ColumnSelectorColumn[] =>
  columns.map(column =>
    column.isReadonly ? column : { ...column, isSelected: true }
  )

export const useManageDrawerColumns = ({
  columns,
  onColumnsChange
}: UseManageDrawerColumnsOptions): UseManageDrawerColumnsResult => {
  const { t } = useAPPTranslation()
  const [isPortalReady, setIsPortalReady] = useState(false)

  useEffect(() => {
    let portalRoot = document.getElementById(SIDE_PANEL_PORTAL_ID)
    const createdPortalRoot = !portalRoot

    if (!portalRoot) {
      portalRoot = document.createElement('div')
      portalRoot.id = SIDE_PANEL_PORTAL_ID
      portalRoot.dataset.columnSelectorPortal = 'true'
      document.body.appendChild(portalRoot)
    }

    setIsPortalReady(true)

    return () => {
      if (createdPortalRoot && portalRoot?.childElementCount === 0) {
        portalRoot.remove()
      }
    }
  }, [])

  const visibleColumnsCount = useMemo(
    () => columns.filter(column => column.isSelected).length,
    [columns]
  )

  const hasSelectableColumns = useMemo(
    () => columns.some(column => !column.isReadonly),
    [columns]
  )

  const areAllSelectableColumnsSelected = useMemo(
    () =>
      hasSelectableColumns &&
      columns
        .filter(column => !column.isReadonly)
        .every(column => column.isSelected),
    [columns, hasSelectableColumns]
  )

  const bulkSelectionLabelKey = useMemo(
    () =>
      areAllSelectableColumnsSelected
        ? 'c:components.ColumnSelector.Drawer.uncheckAll'
        : 'c:components.ColumnSelector.Drawer.checkAll',
    [areAllSelectableColumnsSelected]
  )

  const visibleColumnsLabel = useMemo(
    () =>
      t('c:components.ColumnSelector.Drawer.visibleColumns', {
        count: visibleColumnsCount
      }),
    [visibleColumnsCount, t]
  )

  const handleToggleAllColumns = useCallback(() => {
    if (areAllSelectableColumnsSelected) {
      onColumnsChange(clearSelectableColumns(columns))
      return
    }

    onColumnsChange(selectSelectableColumns(columns))
  }, [areAllSelectableColumnsSelected, columns, onColumnsChange])

  const handleToggleColumn = useCallback(
    (label: string) => {
      const column = columns.find(
        currentColumn => currentColumn.label === label
      )

      if (column?.isReadonly) {
        return
      }

      onColumnsChange(updateColumnSelection(columns, label))
    },
    [columns, onColumnsChange]
  )

  return {
    isPortalReady,
    visibleColumnsCount,
    hasSelectableColumns,
    bulkSelectionLabelKey,
    visibleColumnsLabel,
    handleToggleAllColumns,
    handleToggleColumn
  }
}

export default useManageDrawerColumns
